import { COLORS } from '@/config/colors';
import { WEAPONS } from '@/config/weapons';
import { WEAPON_CATEGORY_COLOR, weaponGlyphKey } from '@/config/weapon-visuals';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/**
 * Per-weapon glyphs (docs/09 "Okunabilirlik"): one silhouette per weapon id,
 * tinted by its category. Drawn in a 40×40 box centred on (0,0), nose → +X.
 * Shared by the `ui/weapon-<id>` icons and the per-weapon crate lids.
 */
type Glyph = (ctx: CanvasRenderingContext2D, c: number) => void;

const LINE = 2.5;

/** Pointed round (casing + ogive tip) — the base "bullet" shape. */
function round(ctx: CanvasRenderingContext2D, c: number, x: number, y: number, len: number, h: number): void {
  const half = h / 2;
  const tip = len * 0.45;
  ctx.beginPath();
  ctx.moveTo(x, y - half);
  ctx.lineTo(x + len - tip, y - half);
  ctx.quadraticCurveTo(x + len, y - half, x + len, y);
  ctx.quadraticCurveTo(x + len, y + half, x + len - tip, y + half);
  ctx.lineTo(x, y + half);
  ctx.closePath();
  fillOutlined(ctx, c, LINE);
  ctx.fillStyle = css(shade(c, 0.62));
  ctx.fillRect(x + 1.5, y - half + 1.5, len * 0.22, h - 3);
}

function speedLines(ctx: CanvasRenderingContext2D, c: number, x: number, ys: ReadonlyArray<number>, len: number): void {
  ctx.strokeStyle = css(shade(c, 0.8));
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  for (const y of ys) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + len, y);
    ctx.stroke();
  }
}

function dashedArc(ctx: CanvasRenderingContext2D, c: number, draw: () => void): void {
  ctx.save();
  ctx.setLineDash([3, 3.5]);
  ctx.strokeStyle = css(shade(c, 0.85));
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  draw();
  ctx.stroke();
  ctx.restore();
}

const GLYPHS: Record<string, Glyph> = {
  // One mid-size round + trail: the all-rounder
  autocannon(ctx, c) {
    speedLines(ctx, c, -19, [-4, 4], 7);
    round(ctx, c, -10, 0, 28, 12);
  },
  // Three small rounds in a staggered burst: spray
  smg(ctx, c) {
    round(ctx, c, -19, 9, 16, 8.5);
    round(ctx, c, -8, -1, 16, 8.5);
    round(ctx, c, 3, -11, 16, 8.5);
  },
  // Thick barrel mouth + a big slug: slow, heavy hits
  heavy_cannon(ctx, c) {
    ctx.beginPath();
    ctx.roundRect(-19, -9, 15, 18, 3);
    fillOutlined(ctx, shade(c, 0.55), LINE);
    ctx.beginPath();
    ctx.roundRect(-7, -11, 5, 22, 2);
    fillOutlined(ctx, shade(c, 0.75), LINE);
    circle(ctx, 9, 0, 10);
    fillOutlined(ctx, c, LINE);
    circle(ctx, 6, -3, 3.5);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fill();
  },
  // Big orb inside an orbit ring
  plasma_orb(ctx, c) {
    ctx.beginPath();
    ctx.ellipse(0, 0, 18, 7, -0.5, 0, Math.PI * 2);
    ctx.strokeStyle = css(shade(c, 0.8));
    ctx.lineWidth = 2.5;
    ctx.stroke();
    circle(ctx, 0, 0, 11);
    fillOutlined(ctx, c, LINE);
    circle(ctx, 0, 0, 5.5);
    ctx.fillStyle = css(shade(c, 1.6));
    ctx.fill();
  },
  // Long spear with a hot plasma head: fast, piercing look
  plasma_lance(ctx, c) {
    ctx.beginPath();
    ctx.moveTo(-19, 0);
    ctx.lineTo(-4, -5);
    ctx.lineTo(19, 0);
    ctx.lineTo(-4, 5);
    ctx.closePath();
    fillOutlined(ctx, shade(c, 0.75), LINE);
    circle(ctx, 8, 0, 6);
    fillOutlined(ctx, c, LINE);
    circle(ctx, 8, 0, 2.5);
    ctx.fillStyle = css(shade(c, 1.7));
    ctx.fill();
  },
  // One hair-thin needle with a tip sparkle: precision stream
  needle_laser(ctx, c) {
    ctx.beginPath();
    ctx.moveTo(-19, -2.5);
    ctx.lineTo(9, -2.5);
    ctx.lineTo(16, 0);
    ctx.lineTo(9, 2.5);
    ctx.lineTo(-19, 2.5);
    ctx.closePath();
    fillOutlined(ctx, c, 2);
    ctx.beginPath();
    ctx.moveTo(16, -8);
    ctx.lineTo(17.5, -1.5);
    ctx.lineTo(19, 0);
    ctx.lineTo(17.5, 1.5);
    ctx.lineTo(16, 8);
    ctx.lineTo(14.5, 1.5);
    ctx.lineTo(10, 0);
    ctx.lineTo(14.5, -1.5);
    ctx.closePath();
    ctx.fillStyle = css(shade(c, 1.5));
    ctx.fill();
  },
  // Three fat pulses + chevron: rhythmic bursts
  pulse_laser(ctx, c) {
    for (const x of [-19, -9, 1]) {
      ctx.beginPath();
      ctx.roundRect(x, -4.5, 8, 9, 3);
      fillOutlined(ctx, c, LINE);
    }
    ctx.beginPath();
    ctx.moveTo(11, -9);
    ctx.lineTo(19, 0);
    ctx.lineTo(11, 9);
    ctx.lineTo(11, 4);
    ctx.lineTo(14, 0);
    ctx.lineTo(11, -4);
    ctx.closePath();
    fillOutlined(ctx, shade(c, 1.2), 2);
  },
  // Bomb at the end of a lobbed dashed arc
  mortar(ctx, c) {
    dashedArc(ctx, c, () => {
      ctx.moveTo(-18, 16);
      ctx.quadraticCurveTo(-12, -14, 2, -4);
    });
    circle(ctx, 7, 5, 10);
    fillOutlined(ctx, shade(c, 0.42), LINE);
    circle(ctx, 3.5, 1.5, 3);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.fill();
    circle(ctx, 14, -4, 3.5);
    ctx.fillStyle = css(c);
    ctx.fill();
  },
  // Finned rocket with exhaust flame
  rocket(ctx, c) {
    ctx.beginPath();
    ctx.moveTo(-18, -3);
    ctx.lineTo(-23, 0);
    ctx.lineTo(-18, 3);
    ctx.closePath();
    ctx.fillStyle = css(COLORS.playerYellow);
    ctx.fill();
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(-16, s * 4);
      ctx.lineTo(-19, s * 11);
      ctx.lineTo(-8, s * 4);
      ctx.closePath();
      fillOutlined(ctx, shade(c, 0.55), 2);
    }
    ctx.beginPath();
    ctx.moveTo(-17, -5);
    ctx.lineTo(8, -5);
    ctx.quadraticCurveTo(19, -4, 20, 0);
    ctx.quadraticCurveTo(19, 4, 8, 5);
    ctx.lineTo(-17, 5);
    ctx.closePath();
    fillOutlined(ctx, c, LINE);
    ctx.fillStyle = css(shade(c, 0.6));
    ctx.fillRect(4, -3.5, 3, 7);
  },
  // Small missile curving into a target reticle
  homing_missile(ctx, c) {
    dashedArc(ctx, c, () => {
      ctx.moveTo(-19, 14);
      ctx.quadraticCurveTo(-14, 2, -6, 2);
    });
    ctx.save();
    ctx.translate(-1, 0);
    ctx.rotate(-0.55);
    ctx.beginPath();
    ctx.moveTo(-6, -3.5);
    ctx.lineTo(4, -3.5);
    ctx.quadraticCurveTo(11, -3, 11, 0);
    ctx.quadraticCurveTo(11, 3, 4, 3.5);
    ctx.lineTo(-6, 3.5);
    ctx.closePath();
    fillOutlined(ctx, c, 2);
    ctx.restore();
    circle(ctx, 12, -9, 7);
    ctx.strokeStyle = css(shade(c, 1.3));
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) {
      ctx.moveTo(12 + dx * 4, -9 + dy * 4);
      ctx.lineTo(12 + dx * 10, -9 + dy * 10);
    }
    ctx.stroke();
  },
};

/** Draw the weapon's glyph centred at (cx, cy); `size` = edge of the 40 px box. */
export function drawWeaponGlyph(
  ctx: CanvasRenderingContext2D,
  weaponId: string,
  cx: number,
  cy: number,
  size: number,
): void {
  const def = WEAPONS[weaponId];
  const glyph = GLYPHS[weaponId];
  if (!def || !glyph) return;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(size / 40, size / 40);
  ctx.lineJoin = 'round';
  glyph(ctx, WEAPON_CATEGORY_COLOR[def.category]);
  ctx.restore();
}

/** Texture edge / glyph box: the 40-unit box is drawn at 1.5× (flame tips fit) */
const GLYPH_TEX = 72;
const GLYPH_BOX = 60;

/** `ui/weapon-<id>` — transparent glyph icon, one per weapon id. */
export const weaponGlyphDefs: ProceduralDef[] = Object.keys(WEAPONS).map((id) => ({
  key: weaponGlyphKey(id),
  width: GLYPH_TEX,
  height: GLYPH_TEX,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    drawWeaponGlyph(ctx, id, w / 2, h / 2, GLYPH_BOX);
  },
}));
